import React from 'react';

import { Icon } from '@/components/shared/element/icons';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Flex } from '@/components/shared/layout/flex';
import { styled } from '@/stitches.config';

const PaginationItem = styled(Box, {
  cursor: 'pointer',
  minWidth: '$size-8',
  minHeight: '$size-8',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transitionProperty: 'background-color',
  transitionDuration: 'var(--transition-duration)',
  transitionTimingFunction: 'var(--transition-easing)',
  '& > *': {
    marginBottom: '-$space-0-5',
  },
  '&.is-current': {
    pe: 'none',
    bc: '$primary',
    '& > *': {
      color: '$white',
    },
  },
  '@hover': {
    '&:hover': {
      bc: '$gray300',
    },
  },
});

const PaginationIcon = styled(Box, {
  cursor: 'pointer',
  '@hover': {
    '&:hover': {
      '& > *': {
        color: '$primary',
      },
    },
  },
});

interface PaginationProps {
  pagination: {
    current: number;
    total: number;
  };
  onClickPage?: (page: number) => void;
  dataTest?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  pagination: { current, total },
  onClickPage,
  dataTest = 'pagination',
  ...rest
}: PaginationProps) => {
  const paginate = (_total: number, page_size: number, _current: number) => {
    const array = Array(_total)
      .fill(null)
      .map((_, i) => i + 1);
    const startIndex =
      _current <= 3
        ? 0
        : _current > _total - 3
          ? _total - page_size
          : _current - 3;
    const stopIndex =
      current < 3
        ? page_size
        : _current > _total - 3
          ? _total
          : page_size + _current - 3;
    return array.slice(Math.max(startIndex, 0), Math.min(stopIndex, _total));
  };

  const handleOnClick = (page: number) => {
    onClickPage && onClickPage(page);
  };

  return (
    <Box
      paddingTop="10"
      test={{ dataTest: `${dataTest}s` }}
      data-page-total={total}
      data-page-current={current}
      {...rest}
    >
      <Container>
        <Flex
          alignItems="center"
          justifyContent="center"
          columnGap={{ '@initial': '2', '@xs': '4' }}
        >
          {total > 5 && current !== 1 && (
            <PaginationIcon
              onClick={
                current !== 1 ? () => handleOnClick(current - 1) : undefined
              }
              test={{
                dataTest: `${dataTest}_button_previous`,
              }}
            >
              <Icon icon="chevron-left" color="icon-gray-light" />
            </PaginationIcon>
          )}
          {paginate(total, 5, current).map((pageNumber, index) => (
            <PaginationItem
              key={index}
              paddingX="1-5"
              paddingY="2"
              rounded="1"
              onClick={() => handleOnClick(pageNumber)}
              className={pageNumber === current ? 'is-current' : ''}
              test={{
                dataTest: `${dataTest}_${index}`,
              }}
            >
              <Text
                variant="text-gray"
                size="text-button-2"
                font="bold"
                data-test={`${dataTest}_number`}
              >
                {pageNumber}
              </Text>
            </PaginationItem>
          ))}
          {total > 5 && current !== total && (
            <PaginationIcon
              onClick={
                current <= total ? () => handleOnClick(current + 1) : undefined
              }
              test={{
                dataTest: `${dataTest}_button_next`,
              }}
            >
              <Icon icon="chevron-right" color="icon-gray-light" />
            </PaginationIcon>
          )}
        </Flex>
      </Container>
    </Box>
  );
};

Pagination.displayName = 'Pagination';
